/* eslint-disable class-methods-use-this -- this adapter is stateless: every
 * method reads the module-level bundled dataset, mirroring the sibling
 * PlentifulLocationsService. It stays a class so the facade can hold it as a
 * swappable, nullable instance alongside the other adapters.
 */
import freshtrakLocations from '../../../constants/freshtrakLocations.json';
import { mergeLocations } from '../../../utils/mergeLocations';

import type { Bbox } from './types';

// FreshTrak ids are minted as `freshtrak-<loc_id>` by the CSV converter, so a
// prefix test is enough to route a detail lookup back to the static dataset.
const FRESHTRAK_ID_PREFIX = 'freshtrak-';

// Green FreshTrak pins take precedence over coincident Plentiful pins, and the
// two sources name the same site differently — so dedup on geography alone at
// a tight "same building" radius rather than on name similarity.
const FRESHTRAK_DEDUP_RADIUS_KM = 0.02;

export function isFreshTrakLocationId(id: number | string | null | undefined): id is string {
  return typeof id === 'string' && id.startsWith(FRESHTRAK_ID_PREFIX);
}

const ALL_LOCATIONS = freshtrakLocations as any[];

/**
 * Static FreshTrak locations overlay. The dataset ships bundled in the app
 * (generated from a periodic CSV export by `scripts/convert-freshtrak-csv.js`),
 * so every method resolves synchronously and never fails.
 */
export class FreshTrakLocationsService {
  async fetchMapLocations(bbox: Bbox): Promise<any[]> {
    const latMin = Math.min(Number(bbox.lat1), Number(bbox.lat2));
    const latMax = Math.max(Number(bbox.lat1), Number(bbox.lat2));
    const lngMin = Math.min(Number(bbox.lng1), Number(bbox.lng2));
    const lngMax = Math.max(Number(bbox.lng1), Number(bbox.lng2));
    return ALL_LOCATIONS.filter(
      (loc) =>
        loc.latitude >= latMin && loc.latitude <= latMax && loc.longitude >= lngMin && loc.longitude <= lngMax
    );
  }

  async fetchLocationById(id: string): Promise<any> {
    return ALL_LOCATIONS.find((loc) => loc.id === id) ?? null;
  }

  /**
   * Combine a Plentiful response with the FreshTrak overlay. FreshTrak wins:
   * every FreshTrak row is kept, and a Plentiful row (whether a Plentiful
   * provider or a scraped "other pantry") is dropped when it sits within ~20m
   * of a FreshTrak row. Both the yellow (Plentiful provider) and blue (scraped)
   * pins survive except where FreshTrak already covers the same site.
   */
  mergeWith(plentifulRows: any[], freshtrakRows: any[]): any[] {
    return mergeLocations(freshtrakRows, plentifulRows, {
      radiusKm: FRESHTRAK_DEDUP_RADIUS_KM,
      requireNameMatch: false,
    });
  }
}

export default FreshTrakLocationsService;
